import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { dgTrack } from "@/lib/dataGraph";
import { readAffiliateParams } from "@/lib/affiliateTracking";
import { VALID_LIFEMART_CODES } from "@/lib/lifemartCodes";
import { activateLifeMartAccess } from "@/lib/lifemart.functions";
import { useMemberPreview } from "@/hooks/useMemberPreview";

// ─── Types ────────────────────────────────────────────────────────────────────

interface LifeMartContextType {
  /** True if the user has an active employer-sponsored LifeMart membership. */
  isLifeMartMember: boolean;
  /** True if the current session originated from a LifeMart affiliate link (sub_id1=LM). */
  isLifeMartAffiliate: boolean;
  /** Either flag — use this to drive co-branding across the funnel. */
  showLifeMartBranding: boolean;
  organizationName: string | null;
  loading: boolean;
  activateAccess: (code: string) => Promise<{ success: boolean; error?: string }>;
  clearAccess: () => void;
}

const LifeMartContext = createContext<LifeMartContextType | undefined>(undefined);

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY   = "lifemart_access_verified";
const SOURCE_KEY    = "lifemart_signup_source";
const VALID_CODES   = VALID_LIFEMART_CODES;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const readSignupSource = (): string => {
  try { return localStorage.getItem(SOURCE_KEY) || "lifemart_generic"; }
  catch { return "lifemart_generic"; }
};

const clearSignupSource = () => {
  try {
    localStorage.removeItem(SOURCE_KEY);
    localStorage.removeItem("lifemart_signup_source_ts");
  } catch {}
};

/** Returns true if the stored affiliate session has sub_id1=LM. */
const checkAffiliateTrigger = (): boolean => {
  try { return readAffiliateParams()?.sub_id1 === "LM"; }
  catch { return false; }
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const LifeMartProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  // Admin "preview as member" mode — hides LifeMart chrome without touching
  // the real verification flag, so exiting the preview restores it instantly.
  const { isPreviewing } = useMemberPreview();
  const [isLifeMartMember,    setIsLifeMartMember]    = useState(false);
  const [isLifeMartAffiliate, setIsLifeMartAffiliate] = useState(false);
  const [organizationName,    setOrganizationName]    = useState<string | null>(null);
  const [loading,             setLoading]             = useState(true);

  useEffect(() => {
    // Affiliate trigger is purely local — no async needed.
    setIsLifeMartAffiliate(checkAffiliateTrigger());

    const checkMembership = async () => {
      try {
        // 1. Auto-apply promo code from URL (?promo= or ?ref=)
        const params  = new URLSearchParams(window.location.search);
        const urlCode = (params.get("promo") || params.get("ref"))?.trim().toUpperCase();
        if (urlCode && (VALID_CODES as readonly string[]).includes(urlCode)) {
          const wasMember = localStorage.getItem(STORAGE_KEY) === "true";
          localStorage.setItem(STORAGE_KEY, "true");
          setIsLifeMartMember(true);
          setOrganizationName("LifeMart");
          if (!wasMember) {
            dgTrack("behavioral", "lifemart_session_start", {
              source: "url_promo",
              code: urlCode,
              entry_path: window.location.pathname,
              signup_source: readSignupSource(),
            });
          }
          // Persist to DB when signed in; localStorage alone is not durable.
          if (user) {
            await activateLifeMartAccess({
              data: { code: urlCode, signupSource: readSignupSource() },
            });
            clearSignupSource();
          }
        }

        // 2. Local storage verification (access code already entered this session)
        if (localStorage.getItem(STORAGE_KEY) === "true") {
          setIsLifeMartMember(true);
          setOrganizationName("LifeMart");
        }

        // 3. DB membership check for authenticated users
        if (user) {
          const { data: membership } = await supabase
            .from("organization_members")
            .select("organization_name, access_code, is_active")
            .eq("user_id", user.id)
            .eq("is_active", true)
            .maybeSingle();

          if (membership) {
            setIsLifeMartMember(true);
            setOrganizationName(membership.organization_name);
          }

          // 4. Affiliate attribution from DB — covers logged-in users whose localStorage was cleared
          const { data: attrs } = await supabase
            .from("user_attributes")
            .select("aff_sub_id1")
            .eq("user_id", user.id)
            .maybeSingle();
          if (attrs?.aff_sub_id1 === "LM") {
            setIsLifeMartAffiliate(true);
          }

          // 5. First-touch tag: persist buffered signup_source on first authenticated load
          const bufferedSource = localStorage.getItem(SOURCE_KEY);
          const localVerified  = localStorage.getItem(STORAGE_KEY) === "true";
          if (bufferedSource && localVerified) {
            await activateLifeMartAccess({
              data: {
                code: membership?.access_code || "LIFEMART2025",
                signupSource: bufferedSource,
              },
            });
            clearSignupSource();
          }
        }
      } finally {
        setLoading(false);
      }
    };

    checkMembership();
  }, [user]);

  const activateAccess = useCallback(async (
    code: string
  ): Promise<{ success: boolean; error?: string }> => {
    const normalized = code.trim().toUpperCase();
    if (!(VALID_CODES as readonly string[]).includes(normalized)) {
      return { success: false, error: "Invalid access code. Please check with your employer or benefits provider." };
    }

    const wasMember = localStorage.getItem(STORAGE_KEY) === "true";
    localStorage.setItem(STORAGE_KEY, "true");
    setIsLifeMartMember(true);
    setOrganizationName("LifeMart");

    if (!wasMember) {
      dgTrack("behavioral", "lifemart_session_start", {
        source:        "manual_code",
        code:          normalized,
        signup_source: readSignupSource(),
      });
    }

    if (user) {
      await activateLifeMartAccess({
        data: { code: normalized, signupSource: readSignupSource() },
      });
      clearSignupSource();
    }

    return { success: true };
  }, [user]);

  const clearAccess = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setIsLifeMartMember(false);
    setOrganizationName(null);
  }, []);

  const effectiveIsMember = isPreviewing ? false : isLifeMartMember;
  const effectiveIsAffiliate = isPreviewing ? false : isLifeMartAffiliate;

  return (
    <LifeMartContext.Provider value={{
      isLifeMartMember: effectiveIsMember,
      isLifeMartAffiliate: effectiveIsAffiliate,
      showLifeMartBranding: effectiveIsMember || effectiveIsAffiliate,
      organizationName: isPreviewing ? null : organizationName,
      loading,
      activateAccess,
      clearAccess,
    }}>
      {children}
    </LifeMartContext.Provider>
  );
};

// ─── Hook ────────────────────────────────────────────────────────────────────────────

export const useLifeMart = () => {
  const context = useContext(LifeMartContext);
  if (!context) throw new Error("useLifeMart must be used within LifeMartProvider");
  return context;
};
